import { allStatuses } from '@utils/helpers';
import { getLocationAsText } from '@src/utils';
import {
  DRIVER_CHAT_GROUP_WINDOW_MS,
  DRIVER_CHAT_IN_PROGRESS_GROUP,
} from '@component/DriverChat/constants';
import {
  ChatMessage,
  ChatSender,
  DriverChatSeed,
} from '@component/DriverChat/types';

type StatusInfo = { value?: string; group?: string } | undefined;

const getStatusInfo = (statusId?: number | null): StatusInfo =>
  statusId == null
    ? undefined
    : (allStatuses as Record<number, StatusInfo>)[statusId];

export const isChatEnabledStatus = (statusId?: number | null) =>
  getStatusInfo(statusId)?.group === DRIVER_CHAT_IN_PROGRESS_GROUP;

export const getStatusLabel = (statusId?: number | null) =>
  getStatusInfo(statusId)?.value ?? '';

export const getInitials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

/** "Joan Delgado" -> "J. D•••••". Revealed only through an explicit Show action. */
export const maskMemberName = (first = '', last = '') => {
  if (!first && !last) return '';
  const lastInitial = last ? `${last[0].toUpperCase()}•••••` : '';
  return `${first ? `${first[0].toUpperCase()}.` : ''} ${lastInitial}`.trim();
};

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'short',
});

const shortTimeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const dayFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

/** "10:04 EDT" — audit timestamps always carry the timezone. */
export const formatChatTime = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : timeFormatter.format(date);
};

export const formatChatShortTime = (iso?: string) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : shortTimeFormatter.format(date);
};

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const getDayLabel = (iso: string) => {
  const date = new Date(iso);
  const diffDays = Math.round(
    (startOfDay(new Date()) - startOfDay(date)) / 86_400_000
  );
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return dayFormatter.format(date);
};

const normalizeName = (name?: string | null) =>
  (name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

export type ChatViewer = {
  firstName?: string;
  lastName?: string;
} | null;

/**
 * Whether a dispatcher message was written by the viewer. Every dispatcher of
 * the payor can write in the same conversation, so DISPATCHER alone is not "me".
 * Users are recognised by name: `senderName` is compared with the viewer's
 * first and last name. A message still being sent (optimistic) is always the
 * viewer's own.
 */
export const isOwnMessage = (message: ChatMessage, viewer: ChatViewer) => {
  if (message.sender !== 'DISPATCHER') return false;
  if (message.id === message.clientMessageId) return true;
  const mine = normalizeName(`${viewer?.firstName ?? ''} ${viewer?.lastName ?? ''}`);
  // Unknown sender name: treat as the viewer's own, as before.
  return !message.senderName || (!!mine && normalizeName(message.senderName) === mine);
};

export type MessageListItem =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'new'; key: string }
  | { kind: 'system'; key: string; message: ChatMessage }
  | {
      kind: 'group';
      key: string;
      sender: Exclude<ChatSender, 'SYSTEM'>;
      /** The viewer's own messages: right side, no name. */
      isOwn: boolean;
      /** Who wrote it, for everyone except the viewer. */
      authorName?: string;
      messages: ChatMessage[];
    };

/**
 * Flattens messages into render items: day separators, a "New messages"
 * divider before `firstUnreadId`, system events, and sender groups.
 */
export const buildMessageListItems = (
  messages: ChatMessage[],
  firstUnreadId: string | null,
  viewer: ChatViewer = null
): MessageListItem[] => {
  const items: MessageListItem[] = [];
  let lastDay = '';
  let currentGroup: Extract<MessageListItem, { kind: 'group' }> | null = null;

  messages.forEach((message) => {
    const day = getDayLabel(message.createdAt);
    if (day !== lastDay) {
      items.push({ kind: 'day', key: `day-${message.id}`, label: day });
      lastDay = day;
      currentGroup = null;
    }
    if (message.id === firstUnreadId) {
      items.push({ kind: 'new', key: `new-${message.id}` });
      currentGroup = null;
    }
    if (message.sender === 'SYSTEM') {
      items.push({ kind: 'system', key: message.id, message });
      currentGroup = null;
      return;
    }
    const isOwn = isOwnMessage(message, viewer);
    const authorName = isOwn ? undefined : message.senderName;
    const previous = currentGroup?.messages[currentGroup.messages.length - 1];
    const fitsGroup =
      currentGroup &&
      currentGroup.sender === message.sender &&
      currentGroup.isOwn === isOwn &&
      currentGroup.authorName === authorName &&
      previous &&
      new Date(message.createdAt).getTime() -
        new Date(previous.createdAt).getTime() <
        DRIVER_CHAT_GROUP_WINDOW_MS;

    if (fitsGroup && currentGroup) {
      currentGroup.messages.push(message);
    } else {
      currentGroup = {
        kind: 'group',
        key: `group-${message.id}`,
        sender: message.sender,
        isOwn,
        authorName,
        messages: [message],
      };
      items.push(currentGroup);
    }
  });

  return items;
};

export const createClientMessageId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `cm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/** Builds the chat seed from a monitoring or ride segment (driver/vehicle already parsed). */
export const buildSeedFromSegment = (segment: any): DriverChatSeed | null => {
  if (!segment?.id) return null;
  const { driver = {}, vehicle = {}, member } = segment;
  const vehicleLabel = [vehicle?.color, vehicle?.make, vehicle?.model]
    .filter(Boolean)
    .join(' ');

  return {
    segmentId: Number(segment.id),
    rideId: segment.rideId != null ? Number(segment.rideId) : null,
    driverFirstName: driver?.first_name ?? '',
    driverImage: driver?.image_url,
    driverPhone: driver?.phone_number,
    vehicleLabel: vehicleLabel || undefined,
    licensePlate: vehicle?.license_plate,
    statusId: segment.statusId != null ? Number(segment.statusId) : null,
    pickupAddress: segment.pickupLocations
      ? getLocationAsText(segment.pickupLocations, true)
      : undefined,
    pickupTime: segment.scheduledPickupTime ?? undefined,
    dropOffAddress: segment.dropOffLocation
      ? getLocationAsText(segment.dropOffLocation, true)
      : undefined,
    dropOffTime: segment.scheduledAppointmentTime ?? undefined,
    memberFirstName: member?.firstName,
    memberLastName: member?.lastName,
  };
};

/** Seed fields win when present: they come from the live segment on screen. */
export const mergeChatSeed = <T extends DriverChatSeed>(
  base: T | undefined,
  seed: DriverChatSeed
): T => {
  const merged = { ...(base ?? {}) } as T;
  (Object.keys(seed) as Array<keyof DriverChatSeed>).forEach((key) => {
    const value = seed[key];
    if (value !== undefined && value !== null && value !== '') {
      (merged as DriverChatSeed)[key] = value as never;
    }
  });
  return merged;
};
